import { Download, File, FileX, Paperclip, RotateCcw, X } from "lucide-react";
import {
  Attachment,
  AttachmentMedia,
  AttachmentContent,
  AttachmentTitle,
  AttachmentDescription,
  AttachmentActions,
  AttachmentAction,
  AttachmentGroup,
  Button,
} from "@opencloud/ui";
import { useRef } from "react";
import type { AgentAttachment } from "@opencloud/agent-protocol";
import type { ChatUploads } from "../types.js";

export function fileSize(bytes: number) {
  return bytes < 1_000
    ? `${bytes} B`
    : bytes < 1_000_000
      ? `${(bytes / 1_000).toFixed(1)} KB`
      : `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export function ChatAttachments({
  files,
  path,
  readOnly = false,
  deletedIds,
}: {
  files: readonly AgentAttachment[];
  path?: string | undefined;
  readOnly?: boolean;
  /** Files an app admin deleted: the message keeps their card, without a download. */
  deletedIds?: readonly string[] | undefined;
}) {
  if (!files.length) return null;
  return (
    <AttachmentGroup
      className="my-2 w-full min-w-0"
      aria-label="Attached files"
    >
      {files.map((file) => {
        const deleted = deletedIds?.includes(file.id) === true;
        return (
          <Attachment
            key={file.id}
            state="done"
            data-deleted={deleted || undefined}
            className={deleted ? "border-dashed bg-transparent text-muted-foreground" : undefined}
          >
            <AttachmentMedia className={deleted ? "text-muted-foreground" : undefined}>
              {deleted ? <FileX aria-hidden="true" /> : <File aria-hidden="true" />}
            </AttachmentMedia>
            <AttachmentContent>
              <AttachmentTitle>{file.name}</AttachmentTitle>
              <AttachmentDescription>
                {deleted ? `${fileSize(file.size)} · Deleted` : fileSize(file.size)}
              </AttachmentDescription>
            </AttachmentContent>
            {!deleted && !readOnly && path ? (
              <AttachmentActions>
                <AttachmentAction
                  nativeButton={false}
                  render={
                    <a
                      href={`${path}/attachments/${encodeURIComponent(file.id)}/download`}
                      download
                    />
                  }
                  aria-label={`Download ${file.name}`}
                >
                  <Download aria-hidden="true" />
                </AttachmentAction>
              </AttachmentActions>
            ) : null}
          </Attachment>
        );
      })}
    </AttachmentGroup>
  );
}

export function DraftAttachments({ uploads }: { uploads: ChatUploads }) {
  return (
    <div aria-live="polite">
      {uploads.items.length ? (
        <AttachmentGroup
          className="mb-2 w-full min-w-0"
          aria-label="Files to send"
        >
          {uploads.items.map((file) => (
            <Attachment key={file.key} state={file.state}>
              <AttachmentMedia>
                <File aria-hidden="true" />
              </AttachmentMedia>
              <AttachmentContent>
                <AttachmentTitle>{file.name}</AttachmentTitle>
                <AttachmentDescription>
                  {fileSize(file.size)} ·{" "}
                  {file.state === "done"
                    ? "Ready"
                    : file.state === "error"
                      ? "Upload failed"
                      : `Uploading ${file.progress}%`}
                </AttachmentDescription>
              </AttachmentContent>
              <AttachmentActions>
                {file.state === "error" ||
                (uploads.error && file.state !== "done") ? (
                  <AttachmentAction
                    aria-label={`Retry ${file.name}`}
                    onClick={() => void uploads.retry(file.key)}
                  >
                    <RotateCcw aria-hidden="true" />
                  </AttachmentAction>
                ) : null}
                <AttachmentAction
                  aria-label={`Remove ${file.name}`}
                  onClick={() => uploads.remove(file.key)}
                >
                  <X aria-hidden="true" />
                </AttachmentAction>
              </AttachmentActions>
            </Attachment>
          ))}
        </AttachmentGroup>
      ) : null}
      {uploads.error ? (
        <p role="alert" className="mb-2 text-sm text-destructive">
          {uploads.error}
        </p>
      ) : null}
    </div>
  );
}

export function AttachFilesButton({
  uploads,
  disabled,
}: {
  uploads: ChatUploads;
  disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label="Choose files"
        onChange={(event) => {
          void uploads.add(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Attach files"
        title="Attach files (up to 100 MB each)"
        disabled={disabled || uploads.preparing}
        onClick={() => input.current?.click()}
      >
        <Paperclip aria-hidden="true" />
      </Button>
    </>
  );
}
